import { QueryKeys } from '@/enums';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import { IProfileAddressModel, IProfileModel, IRewardPoint, IRewardPointTransaction } from '@/interfaces/profile';
import {
  deleteProfileAddress,
  getProfile,
  getProfileAddressList,
  getRewardPoint,
  getRewardPointsTransaction,
  postFeedback,
  updateProfile,
  updateProfileAddress,
} from '@/services/profile.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation(updateProfile, {
    onSuccess: (data) => {
      openNotification('success', 'Profile', 'Profile Update Successfully');
      queryClient.invalidateQueries([QueryKeys.profileData]);
    },
    onError: (data: any) => {
      // openNotification('danger', 'Profile', data.error);
    },
  });
};

export const useGetProfileAddressList = (session?: INextAuthSessionModel | null) => {
  return useQuery<IProfileAddressModel[]>(
    [QueryKeys.addressLists],
    async () => {
      const addressList = await getProfileAddressList(session);
      return addressList;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!session,
    },
  );
};
export const useGetProfile = (session?: INextAuthSessionModel | null, shouldCallProfile: boolean = true) => {
  return useQuery<IProfileModel>(
    [QueryKeys.profileData],
    async () => {
      const profile = await getProfile(session?.accessToken);
      return profile;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!session && shouldCallProfile,
    },
  );
};
export const useUpdateProfileAddress = (
  setLoading: Function,
  setShowAddressForm: Function,
  setSelectedShippingAddress?: Function,
  homeDeliveryPageShippingAddress?: boolean,
  homeDeliveryPageBillingAddress?: boolean,
  setSelectedBillingAddress?: Function,
) => {
  const queryClient = useQueryClient();
  return useMutation(updateProfileAddress, {
    onSuccess: (data) => {
      if (homeDeliveryPageShippingAddress) {
        setSelectedShippingAddress?.(data);
      }
      if (homeDeliveryPageBillingAddress) setSelectedBillingAddress?.(data);

      queryClient.invalidateQueries([QueryKeys.addressLists]);
      setLoading(false);
      setShowAddressForm(false);
      openNotification('success', 'Address', 'Profile Address Updated Successfully');
    },
    onError: (data: any) => {
      setLoading(false);
      openNotification('danger', 'Update Error', data.data.Messages[0]);
    },
  });
};
export const useDeleteAddress = (customerId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (addressId: string) => {
      return deleteProfileAddress(addressId, customerId);
    },
    {
      onSuccess: (data) => {
        openNotification('success', 'Address', 'Address Deleted Successfully');
        queryClient.invalidateQueries([QueryKeys.addressLists]);
      },
      onError: (data: any) => {
        openNotification('danger', 'Delete Error', data.data.Messages[0]);
      },
    },
  );
};
export const useGetRewardPoint = (session?: INextAuthSessionModel | null) => {
  return useQuery<IRewardPoint>(
    [QueryKeys.rewardPoints],
    async () => {
      const rewardPoint = await getRewardPoint(session);
      return rewardPoint;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!session,
    },
  );
};
export const useGetRewardPointTransaction = (
  pageSize: number,
  pageNumber: number,
  session?: INextAuthSessionModel | null,
  showPurchaseOnly?: boolean,
) => {
  return useQuery<IRewardPointTransaction>(
    [QueryKeys.rewardPointsTransaction + pageNumber],
    async () => {
      const rewardPointsTransaction = await getRewardPointsTransaction(pageSize, pageNumber, session, showPurchaseOnly);
      return rewardPointsTransaction;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!session,
      keepPreviousData: true,
    },
  );
};

export const usePostFeedback = () => {
  const queryClient = useQueryClient();
  return useMutation(postFeedback);
};
